// src/components/Layout.js
import React, { useState, useEffect, lazy, Suspense, useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import syncService from '../services/syncService';
import userDataService from '../services/userDataService';
// Toggle personalizado
import ThemeModeSwitch from './ui/ThemeModeSwitch';
import { ColorModeContext } from '../context/ThemeModeContext';
import PremiumSidebar from './ui/PremiumSidebar';
import CollapsibleSidebar from './ui/CollapsibleSidebar';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDiagnostic, DiagnosticProvider } from '../context/DiagnosticContext';
import { useOAuth2Monitor } from '../hooks/useOAuth2Monitor';
import OAuth2StatusPanel from './monitoring/OAuth2StatusPanel';

// Lazy load del componente de diagnóstico para optimizar rendimiento
const CorsProxyDiagnostic = lazy(() => import('./CorsProxyDiagnostic'));
const TelemetryDashboard = lazy(() => import('./TelemetryDashboard'));

/**
 * Componente principal de Layout que implementa el patrón de presentación
 * @returns {JSX.Element} Elemento JSX del layout
 */
function LayoutContent() {
  const { userData, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { expanded } = useSidebar();
  const [menuOpen, setMenuOpen] = useState(false);
  // Estado de sincronización global
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });
  // Determinar storeId desde el servicio centralizado o contexto como respaldo
  const storeId = userDataService?.getStoreId?.() || userData?.tiendaId || userData?.tiendas?.[0]?.id;
  // Tema claro/oscuro
  const theme = useTheme();
  const { toggleColorMode } = useContext(ColorModeContext);
  
  // Usar contexto de diagnóstico
  const {
    showCorsProxyDiagnostic,
    toggleCorsProxyDiagnostic,
    diagnosticPermissions,
    updateDiagnosticPermissions
  } = useDiagnostic();
  
  // Monitoreo OAuth2 global
  const { 
    metrics: oauth2Metrics, 
    isCritical, 
    hasWarnings, 
    getRecentErrors 
  } = useOAuth2Monitor();
  
  // Actualizar permisos de diagnóstico cuando cambian los datos de usuario
  useEffect(() => {
    if (userData) {
      updateDiagnosticPermissions(userData);
    }
  }, [userData, updateDiagnosticPermissions]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
};

  // Disparar sincronización global de datos de forma asíncrona
  const handleGlobalSync = async () => {
    if (syncing || !storeId) return;
    setSyncing(true);
    try {
      await syncService.processSync({ tiendaId: storeId, entityType: 'all', options: { forceRefresh: true } });
      setSnackbar({ open: true, msg: 'Sincronización iniciada', severity: 'success' });
    } catch (error) {
      console.error('Error al iniciar sincronización', error);
      setSnackbar({ open: true, msg: `Error al sincronizar: ${error.message}`, severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  // Determinar el nombre de la página actual para mostrar en el encabezado
  const getPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path === '/orders') return 'Órdenes';
    if (path === '/products') return 'Productos';
    
    if (path === '/stats') return 'Estadísticas';
    if (path === '/carts') return 'Carritos Abandonados';
    if (path === '/alerts') return 'Alertas';
    if (path === '/settings') return 'Configuración';
    return 'Nubelytics';
  };

  return (
    <div className="app-container">
      {/* Collapsible Sidebar */}
      <CollapsibleSidebar userData={userData} />
      
      {/* Sidebar Legacy (oculto) */}
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`} style={{ display: 'none' }}>
        <div className="sidebar-header">
          <h1>Nubelytics</h1>
          <button className="close-menu" onClick={closeMenu}>×</button>
        </div>

        <div className="user-info">
          <div className="store-icon">
            {userData?.tiendaName ? userData.tiendaName.charAt(0).toUpperCase() : 'N'}
          </div>
          <div className="store-details">
            <p className="store-name">{userData?.tiendaName || 'Mi Tienda'}</p>
            <span className="plan-badge">{userData?.plan || 'Free'}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <ul>
            <li>
              <Link to="/" onClick={closeMenu} className={location.pathname === '/' ? 'active' : ''}>
                <i className="icon">📊</i> Dashboard
              </Link>
            </li>
            <li>
              <Link to="/orders" onClick={closeMenu} className={location.pathname === '/orders' ? 'active' : ''}>
                <i className="icon">🛍️</i> Órdenes
              </Link>
            </li>
            <li>
              <Link to="/products" onClick={closeMenu} className={location.pathname.startsWith('/products') ? 'active' : ''}>
                <i className="icon">📦</i> Productos
              </Link>
            </li>
            <li>
              <Link to="/stats" onClick={closeMenu} className={location.pathname === '/stats' ? 'active' : ''}>
                <i className="icon">📈</i> Estadísticas
              </Link>
            </li>
            <li>
              <Link to="/carts" onClick={closeMenu} className={location.pathname === '/carts' ? 'active' : ''}>
                <i className="icon">🛒</i> Carritos Abandonados
              </Link>
            </li>
            <li>
              <Link to="/alerts" onClick={closeMenu} className={location.pathname === '/alerts' ? 'active' : ''}>
                <i className="icon">🔔</i> Alertas
              </Link>
            </li>
            <li>
              <Link to="/settings" onClick={closeMenu} className={location.pathname === '/settings' ? 'active' : ''}>
                <i className="icon">⚙️</i> Configuración
              </Link>
            </li>
                      <li>
              <Link to="/billing" onClick={closeMenu} className={location.pathname === '/billing' ? 'active' : ''}>
                <i className="icon">💳</i> Planes
              </Link>
            </li>
                      <li>
              <button onClick={handleGlobalSync} className="nav-sync-button" disabled={syncing}>
                <i className="icon">{syncing ? '⏳' : '🔄'}</i> Sincronizar
              </button>
            </li>
          </ul>
        </nav>

        <div className="sidebar-footer">

          {/* Panel de administración y herramientas solo visible para admins */}
          {diagnosticPermissions.canViewCorsProxy && (
            <div className="admin-tools">
              <button className="diagnostic-button" onClick={toggleCorsProxyDiagnostic}>
                <i className="icon">🔧</i> Diagnóstico CORS
              </button>
            </div>
          )}
          <button className="logout-button" onClick={handleLogout}>
            <i className="icon">🚪</i> Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content" style={{ marginLeft: expanded ? '280px' : '80px', transition: 'margin-left 0.3s ease' }}>
        <header className="main-header">
          {/* Menu toggle oculto - ahora usamos sidebar colapsable */}
          <button className="menu-toggle" onClick={() => setMenuOpen(true)} style={{ display: 'none' }}>
            <span className="menu-icon">≡</span>
          </button>
          <h2>{getPageName()}</h2>
          <div className="header-actions">
            {/* Panel de estado OAuth2 global */}
            <OAuth2StatusPanel compact={true} />
            
            <ThemeModeSwitch checked={theme.palette.mode === 'dark'} onChange={toggleColorMode} />
            {/* Indicador de herramientas de diagnóstico en la cabecera para admins */}
            {diagnosticPermissions.isAdmin && (
              <div className="admin-indicator">
                <button 
                  className="admin-diagnostic-toggle" 
                  title="Herramientas de diagnóstico" 
                  onClick={toggleCorsProxyDiagnostic}
                >
                  🔧
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="content">
          <Outlet />
          
          {/* Componente de diagnóstico CORS con carga lazy */}
          {showCorsProxyDiagnostic && (
            <Suspense fallback={<div className="loading-diagnostic">Cargando herramientas de diagnóstico...</div>}>
              <div className="admin-diagnostic-container">
                <CorsProxyDiagnostic />
                {diagnosticPermissions.isAdmin && (
                  <TelemetryDashboard />
                )}
              </div>
            </Suspense>
          )}
        </div>
      </main>

      {/* Overlay para cerrar el menú en móviles */}
      {menuOpen && <div className="sidebar-overlay" onClick={closeMenu}></div>}

      {/* CSS in JS para el layout */}
      <style jsx="true">{`
        .app-container {
          display: flex;
          width: 100%;
          min-height: 100vh;
        }

        .sidebar {
          width: 260px;
          background-color: #2c3e50;
          color: white;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          z-index: 1000;
          transition: transform 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .sidebar-header {
          padding: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .sidebar-header h1 {
          margin: 0;
          font-size: 1.5rem;
          color: #3498db;
        }

        .close-menu {
          display: none;
          background: none;
          border: none;
          color: white;
          font-size: 1.5rem;
          cursor: pointer;
        }

        .user-info {
          padding: 15px 20px;
          display: flex;
          align-items: center;
          border-bottom: 1px solid #34495e;
        }

        .store-icon {
          width: 40px;
          height: 40px;
          background-color: #3498db;
          color: white;
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          font-weight: bold;
          margin-right: 10px;
        }

        .store-details {
          flex: 1;
        }

        .store-name {
          margin: 0;
          font-weight: 500;
        }

        .plan-badge {
          background-color: #16a085;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.75rem;
        }

        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding-top: 10px;
        }

        .sidebar-nav ul {
          list-style-type: none;
          padding: 0;
          margin: 0;
        }

        .sidebar-nav li a {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          color: #ecf0f1;
          text-decoration: none;
          transition: background-color 0.2s;
        }

        .sidebar-nav li a:hover, .sidebar-nav li a.active {
          background-color: #34495e;
        }

        .icon {
          margin-right: 10px;
          font-size: 1.1rem;
        }

        .sidebar-footer {
          padding: 20px;
          border-top: 1px solid #34495e;
        }

        .logout-button {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 10px;
          background-color: #e74c3c;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .logout-button:hover {
          background-color: #c0392b;
        }

        .main-content {
          flex: 1;
          margin-left: 260px;
          min-height: 100vh;
          background-color: #f5f5f5;
          transition: margin-left 0.3s ease;
        }

        .main-header {
          height: 60px;
          background-color: white;
          display: flex;
          align-items: center;
          padding: 0 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          justify-content: space-between;
        }
        
        .admin-indicator {
          margin-left: auto;
          display: flex;
          align-items: center;
        }
        
        .admin-diagnostic-toggle {
          background: #3498db;
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }
        
        .admin-diagnostic-toggle:hover {
          background: #2980b9;
        }
        
        .admin-tools {
          margin-bottom: 10px;
          padding: 10px 0;
          border-bottom: 1px solid #34495e;
        }
        
        .diagnostic-button {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 10px;
          background-color: #16a085;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-bottom: 10px;
        }
        
        .diagnostic-button:hover {
          background-color: #1abc9c;
        }
        
        .diagnostic-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0,0,0,0.5);
          z-index: 2000;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow-y: auto;
          padding: 20px;
        }
        
        .loading-diagnostic {
          background: white;
          padding: 20px;
          border-radius: 5px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          text-align: center;
        }

        .menu-toggle {
          display: none;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          margin-right: 15px;
        }

        .content {
          padding: 20px;
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0,0,0,0.5);
          z-index: 900;
        }

        /* Sync button */
        .nav-sync-button {
          background: none;
          border: none;
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          color: inherit;
          width: 100%;
          padding: 10px 0;
          text-align: left;
        }
        .nav-sync-button .icon {
          margin-right: 8px;
          display: inline-block;
          width: 20px; /* igualar ancho de emoji links */
          text-align: center;
        }
        .nav-sync-button:disabled { opacity: 0.6; cursor: not-allowed; }
        /* Responsive */
        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
            width: 80%;
            max-width: 300px;
          }
          
          .sidebar.open {
            transform: translateX(0);
          }
          
          .close-menu, .menu-toggle {
            display: block;
          }
          
          .main-content {
            margin-left: 0;
          }
          
          .sidebar-overlay {
            display: block;
          }
        }
      `}</style>
      {snackbar.open && (
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.msg}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
}

/**
 * Layout principal con Provider de diagnóstico integrado
 * Implementa patrón Decorator para envolver el componente con el contexto necesario
 */
function Layout() {
  return (
    <SidebarProvider>
      <DiagnosticProvider>
        <LayoutContent />
      </DiagnosticProvider>
    </SidebarProvider>
  );
}

export default Layout;


